import { giphyLocalUrl } from '@/app/util/env'
import { Cookies, GET } from '@/app/util/url'
import { appendParams, convertToPagination } from '@/util/url'
import { PaginationOptions, Result } from '@giphy/js-fetch-api'
import { IChannel, IGif } from '@giphy/js-types'
import { timeoutFetch } from 'utils/src/api/fetch'
import { IProfileUser } from 'types/src/user'

// The Web channels API uses UserSerializer, which includes is_upgraded.
export interface ChannelResult extends IChannel {
    user: IChannel['user'] & Pick<IProfileUser, 'is_upgraded'>
}

interface CountryOptions {
    country?: string
}
export interface GenericResult<T> extends Result {
    data: T[]
}

export const BASE_PATHNAME = `/api/v4/`

export const channelsUrl = `${BASE_PATHNAME}channels/`

import { getCookie, getPost } from '@/util/url'
import { GifID } from '@giphy/js-types'

const isEmptyFileEntry = (value: FormDataEntryValue) =>
    typeof File !== 'undefined' && value instanceof File && value.name === '' && value.size === 0

const removeEmptyFileEntries = (formData: FormData) => {
    const sanitizedFormData = new FormData()

    formData.forEach((value, key) => {
        if (!isEmptyFileEntry(value)) {
            sanitizedFormData.append(key, value)
        }
    })

    return sanitizedFormData
}

export const moveChannel = async (
    channelId: number | string,
    siblingId: number | string,
    side: 'left' | 'right' | 'first-child' | 'last-child'
) => {
    const csrftoken = getCookie('csrftoken')
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channels/${channelId}/move/`,
        options: {
            ...getPost(csrftoken),
            body: JSON.stringify({ position: side, target_channel: siblingId }),
        },
    })
}

export const editChannel = async (id: number | string, formData: FormData) => {
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channels/${id}`,
        options: {
            ...getPost(getCookie('csrftoken'), true),
            method: 'PATCH',
            body: removeEmptyFileEntries(formData),
        },
    })
}

export const deleteChannelBanner = async (id: number | string) => {
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channels/${id}/`,
        options: {
            method: 'PATCH',
            body: new URLSearchParams({ id: String(id), banner_image: '' }).toString(),
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
                'X-CSRFToken': getCookie('csrftoken'),
                'X-Requested-With': 'XMLHttpRequest',
            },
            credentials: 'same-origin',
        },
    })
}

export const editChannelJson = async (id: number | string, json: Object) => {
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channels/${id}`,
        options: {
            ...getPost(getCookie('csrftoken')),
            method: 'PATCH',
            body: JSON.stringify(json),
        },
    })
}

export const deleteChannel = async (id: string | number) => {
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channels/${id}`,
        options: {
            ...getPost(getCookie('csrftoken')),
            method: 'DELETE',
        },
    })
}

type GifChannelInfo = { channel: IChannel; gif: IGif }

export const associatedChannels = async (gifId: GifID): Promise<GifChannelInfo[]> => {
    const result = await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channel-gifs/?gif=${gifId}`,
    })
    return (await result.json()).results
}
export async function addGifsToChannel(gifIds: GifID[], channelId: number) {
    const csrftoken = getCookie('csrftoken')
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channel-gifs`,
        options: {
            ...getPost(csrftoken),
            body: JSON.stringify({
                items: gifIds.map((gifId) => ({
                    gif: gifId,
                    channel: channelId,
                })),
            }),
        },
    })
}

export async function removeGifsFromChannel(gifIds: GifID[], channelId: number) {
    const csrftoken = getCookie('csrftoken')
    return await timeoutFetch({
        url: `${giphyLocalUrl}/api/v4/channel-gifs/remove`,
        options: {
            ...getPost(csrftoken),
            body: JSON.stringify({
                items: gifIds.map((gifId) => ({
                    gif: gifId,
                    channel: channelId,
                })),
            }),
        },
    })
}

type DeactivateChannelProps = {
    note: string
    reason: string
    reporter: string
    send_email: boolean
    userToDeactivate: string
}
export const deactivateChannel = async ({
    note,
    reason,
    reporter,
    send_email,
    userToDeactivate,
}: DeactivateChannelProps) => {
    try {
        const params = {
            note,
            reason,
            reporter,
            send_email,
            userToDeactivate,
        }

        const response = await fetch(`${giphyLocalUrl}/api/v3/deactivate-user`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
            },
            body: JSON.stringify(params),
        })

        if (!response.ok) {
            throw new Error(`Failed to deactivate channel. Status: ${response.status}`)
        }

        const data = await response.json()

        if (data?.success) {
            return data
        } else {
            throw new Error(data?.message || 'Unknown error occurred during deactivation.')
        }
    } catch (error) {
        console.error('Error deactivating channel:', error)
        throw new Error('There was an issue deactivating the channel.')
    }
}

const getResult = async <T>(
    pathname: string,
    qs: { offset?: number | undefined; [key: string]: any } = {},
    cookie?: Cookies,
    timeout?: number
) => {
    const url = appendParams(new URL(`${BASE_PATHNAME}${pathname}`, giphyLocalUrl), qs)
    const f = await timeoutFetch({ url: url, options: GET(cookie), timeout: timeout })
    if (!f.ok) {
        console.error(`Failed to fetch ${url}`)
    }
    const result = await f.json()
    return convertToPagination(result, qs.offset || 0) as GenericResult<T>
}

export type ArtistChannelsOptions = {
    filter?: string
    freelance?: boolean
    order?: 'asc' | 'desc'
    sort?: 'updatetime' | 'username'
} & PaginationOptions

export const getArtists = async (options: ArtistChannelsOptions) => {
    return getResult<IChannel>(`artists`, options)
}

type ChannelClipsOptions = PaginationOptions & CountryOptions
export const getChannelClipsByUsername = async (username: string, options: ChannelClipsOptions, cookie?: Cookies) => {
    return getResult<IGif>(`channels/${username}/clips/feed`, options, cookie)
}

export const getFeaturedChannels = async () => {
    return getResult<{
        id: number
        slug: string
        url: string
        display_name: string
        avatar_url: string
    }>(`channels/featured-channels`)
}

export const getFeaturedTags = async (options: PaginationOptions = {}) => {
    return getResult<{
        channel_slug: string
        id: number
        name_encoded: string
        name: string
        is_featured: boolean
        is_nsfw: boolean
        is_hidden: boolean
        create_datetime: string
        update_datetime: string
        count: number
    }>(`channels/featured-tags`, options)
}

export type ChannelGifsOptions = PaginationOptions & CountryOptions
export const getChannelGifsById = async (id: number, options: ChannelGifsOptions, cookie?: Cookies) => {
    return getResult<IGif>(`channels/${id}/feed`, options, cookie, 5000)
}

type ChannelOptions = { fetch_children?: boolean; fetch_ranked_tags?: boolean; slug?: string } & PaginationOptions &
    CountryOptions
export const getChannelByUsername = async (options: ChannelOptions = {}, cookie?: Cookies) => {
    const url = appendParams(new URL(channelsUrl, giphyLocalUrl), options)
    const f = await timeoutFetch({ url: url, options: GET(cookie), timeout: 2500 })
    return (await f.json()) as ChannelResult
}

type ChannelSearchOptions = { tagged?: boolean; q?: string } & PaginationOptions
export const getChannelSearch = async (id: number, options: ChannelSearchOptions = {}, cookie: Cookies) => {
    return getResult<IGif>(`channels/${id}/search/`, options, cookie)
}

type ChannelChildren = { fetch_associated_channels?: boolean } & PaginationOptions & CountryOptions
export const fetchChannelChildren = async (
    id: number | string,
    cookie: Cookies = '',
    options: ChannelChildren = {}
) => {
    return getResult<IChannel>(`channels/${id}/children`, options, cookie)
}

export const fetchChannelClips = async (username: string, options?: PaginationOptions) => {
    const gifs = await fetch(
        appendParams(new URL(`${giphyLocalUrl}/api/v4/channels/${username}/clips/feed/`), options || {})
    )
    return convertToPagination<IGif>(await gifs.json(), options?.offset || 0)
}

export const getChannelById = async (id: number, cookies?: Cookies) => {
    const url = new URL(`${channelsUrl}${id}`, giphyLocalUrl)
    const f = await timeoutFetch({ url: url, options: GET(cookies) })
    return (await f.json()) as ChannelResult
}
